export interface SubscriptionRecord {
  id?: string;
  subscription_type?: string;
  billing_cycle?: string;
  status?: string;
  end_date?: string | null;
  amount?: number;
  actual_amount?: number;
  metadata?: {
    extended_from_referral?: boolean;
    bonus_days?: number;
  };
}

const BILLING_CYCLE_STORAGE_KEY = "tellerwand_subscription_billing_cycle";

const PREMIUM_SUBSCRIPTION_TYPES = new Set(["premium", "monthly", "annual", "paid"]);

export function isSubscriptionExpired(endDate?: string | null): boolean {
  if (!endDate) {
    return false;
  }
  return new Date(endDate) < new Date();
}

export function isSubscriptionActive(subscription?: SubscriptionRecord | null): boolean {
  if (!subscription) {
    return false;
  }
  if (subscription.status !== "active") {
    return false;
  }
  return !isSubscriptionExpired(subscription.end_date);
}

function hasPremiumBillingCycle(subscription: SubscriptionRecord): boolean {
  return subscription.billing_cycle === "monthly" || subscription.billing_cycle === "annual";
}

function hasPremiumSubscriptionType(subscription: SubscriptionRecord): boolean {
  return PREMIUM_SUBSCRIPTION_TYPES.has(subscription.subscription_type ?? "");
}

/** True when the user has an active paid or complimentary premium subscription. */
export function isPaidSubscription(subscription?: SubscriptionRecord | null): boolean {
  if (!subscription || !isSubscriptionActive(subscription)) {
    return false;
  }
  return hasPremiumSubscriptionType(subscription) || hasPremiumBillingCycle(subscription);
}

export function isCurrentMonthlyPlan(subscription?: SubscriptionRecord | null): boolean {
  return isPaidSubscription(subscription) && subscription?.billing_cycle === "monthly";
}

export function isCurrentAnnualPlan(subscription?: SubscriptionRecord | null): boolean {
  return isPaidSubscription(subscription) && subscription?.billing_cycle === "annual";
}

export function getEffectiveAmount(subscription?: SubscriptionRecord | null): number {
  return subscription?.actual_amount ?? subscription?.amount ?? 0;
}

/** Active premium access with no charge on file (referral, admin grant, etc.). */
export function isComplimentarySubscription(subscription?: SubscriptionRecord | null): boolean {
  return isPaidSubscription(subscription) && getEffectiveAmount(subscription) === 0;
}

/**
 * Get Plan Display Name.
 */
export function getPlanDisplayName(subscription?: SubscriptionRecord | null): string {
  if (!isPaidSubscription(subscription)) {
    return "Free";
  }

  const amount = getEffectiveAmount(subscription);
  const complimentary = amount === 0;

  if (subscription?.billing_cycle === "monthly") {
    return complimentary ? "Premium (Complimentary)" : "Monthly Premium";
  }
  if (subscription?.billing_cycle === "annual") {
    return complimentary ? "Premium (Complimentary)" : "Annual Premium";
  }
  if (subscription && hasPremiumSubscriptionType(subscription)) {
    return complimentary ? "Premium (Complimentary)" : "Premium";
  }
  return "Free";
}

export function getAmountDisplayLabel(subscription?: SubscriptionRecord | null): string {
  if (isPaidSubscription(subscription) && getEffectiveAmount(subscription) === 0) {
    return "Complimentary";
  }
  return `₹${getEffectiveAmount(subscription)}`;
}

export function getAmountPeriodLabel(subscription?: SubscriptionRecord | null): string {
  if (!isPaidSubscription(subscription)) {
    return "₹0";
  }
  if (getEffectiveAmount(subscription) === 0) {
    return "Complimentary";
  }
  const cycle = subscription?.billing_cycle === "annual" ? "year" : "month";
  return `₹${getEffectiveAmount(subscription)}/${cycle}`;
}

export function formatPlanPriceForComparison(subscription?: SubscriptionRecord | null): string {
  if (!isPaidSubscription(subscription)) {
    return "₹0";
  }
  if (getEffectiveAmount(subscription) === 0) {
    return "Complimentary";
  }
  return `₹${getEffectiveAmount(subscription)}`;
}

/**
 * Get Renewal Label.
 */
export function getRenewalLabel(subscription?: SubscriptionRecord | null): string {
  if (isComplimentarySubscription(subscription) && !subscription?.end_date) {
    return "Complimentary access";
  }
  if (!subscription?.end_date) {
    return isPaidSubscription(subscription)
      ? "No renewal date on file"
      : "No active paid subscription";
  }
  return subscription.status === "active" && !isSubscriptionExpired(subscription.end_date)
    ? "Renews On"
    : "Expires On";
}

/**
 * Get Subscription Renewal Date Display.
 */
export function getSubscriptionRenewalDateDisplay(
  subscription?: SubscriptionRecord | null
): string {
  if (subscription?.end_date) {
    return subscription.end_date;
  }
  if (isComplimentarySubscription(subscription)) {
    return "Complimentary — no billing cycle";
  }
  if (isPaidSubscription(subscription)) {
    return "No renewal date on file";
  }
  return "No active subscription";
}

export function formatAnnualMonthlyEquivalent(annualPrice: number): string {
  const monthly = annualPrice / 12;
  const formatted = Number.isInteger(monthly) ? String(monthly) : monthly.toFixed(2);
  return `≈ ₹${formatted}/mo billed annually`;
}

export function calculateAnnualSavingsPercent(monthlyPrice: number, annualPrice: number): number {
  if (monthlyPrice <= 0) {
    return 0;
  }
  return Math.round(((monthlyPrice * 12 - annualPrice) / (monthlyPrice * 12)) * 100);
}

export function getStoredBillingCyclePreference(): "monthly" | "annual" | null {
  if (typeof window === "undefined") {
    return null;
  }
  const value = window.localStorage.getItem(BILLING_CYCLE_STORAGE_KEY);
  return value === "monthly" || value === "annual" ? value : null;
}

export function setStoredBillingCyclePreference(cycle: "monthly" | "annual"): void {
  if (typeof window === "undefined") {
    return;
  }
  window.localStorage.setItem(BILLING_CYCLE_STORAGE_KEY, cycle);
}

export function resolveInitialBillingCycle(
  subscription?: SubscriptionRecord | null
): "monthly" | "annual" {
  if (subscription?.billing_cycle === "annual") {
    return "annual";
  }
  return getStoredBillingCyclePreference() ?? "monthly";
}

export interface NamedPlanRecord {
  id: string;
  name: string;
}

function matchesPlanCycle(plan: NamedPlanRecord, cycle: "monthly" | "annual"): boolean {
  const id = plan.id.toLowerCase();
  const name = plan.name.toLowerCase();
  return id === cycle || id.includes(cycle) || name === cycle || name.includes(cycle);
}

export function findMonthlyPlan<T extends NamedPlanRecord>(plans: T[]): T | undefined {
  return plans.find((plan) => matchesPlanCycle(plan, "monthly"));
}

export function findAnnualPlan<T extends NamedPlanRecord>(plans: T[]): T | undefined {
  return plans.find((plan) => matchesPlanCycle(plan, "annual"));
}

export function getSubscriptionTierLabel(
  subscription?: SubscriptionRecord | null
): "free" | "paid" {
  return isPaidSubscription(subscription) ? "paid" : "free";
}

export interface SubscriptionStatusSnapshot {
  isPaid: boolean;
  tier: string;
  planDisplayName: string;
  endDate: string | null;
}

/**
 * Build Subscription Status Snapshot.
 */
export function buildSubscriptionStatusSnapshot(
  subscription?: SubscriptionRecord | null
): SubscriptionStatusSnapshot {
  if (!subscription) {
    return {
      isPaid: false,
      tier: "free",
      planDisplayName: "Free",
      endDate: null,
    };
  }

  const isPaid = isPaidSubscription(subscription);
  const planDisplayName = getPlanDisplayName(subscription);
  const tier = isPaid
    ? subscription.billing_cycle || subscription.subscription_type || "premium"
    : "free";

  return {
    isPaid,
    tier,
    planDisplayName,
    endDate: subscription.end_date ?? null,
  };
}

/**
 * Normalize Subscription Status Snapshot.
 */
function normalizeSubscriptionStatusSnapshot(
  cached: Partial<SubscriptionStatusSnapshot>
): SubscriptionStatusSnapshot {
  const isPaid = cached.isPaid ?? false;

  return {
    isPaid,
    tier: cached.tier ?? "free",
    endDate: cached.endDate ?? null,
    planDisplayName: cached.planDisplayName ?? (isPaid ? "Premium" : "Free"),
  };
}

export function toSubscriptionStatusSnapshot(
  cached: Partial<SubscriptionStatusSnapshot> | null | undefined
): SubscriptionStatusSnapshot | undefined {
  if (!cached) {
    return undefined;
  }

  return normalizeSubscriptionStatusSnapshot(cached);
}
